import { Site, SiteAggregateData } from '@domain/graphql.types';
import { Box, Divider, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import type { MutableRefObject } from 'react';
import SiteLookupMapContext from '../context/SiteLookupMapContext';
import { ChevronRight, ExpandMore } from '@mui/icons-material';
import { HistogramBucket, HistoricalUtilizationHistogram } from '../components/HistoricalViewContainer';
import LogoParen from '@application/assets/LogoParen';
import { useTranslation } from 'react-i18next';
import { computeRobustAverage } from './pricingAggregation';

type Props = {
  sites: readonly Site[];
  aggregateData?: SiteAggregateData | null;
};

type OverviewData = {
  numSites: number;
  numSitesTracked: number;
  numPorts: number;
  numPortsTracked: number;
  avgPrice: number | undefined;
};

const OverviewCard = forwardRef<HTMLDivElement, Props>(({ sites, aggregateData }: Props, ref) => {
  const { t } = useTranslation();
  const { borderRadius } = useTheme();
  const { selectedSite, setSelectedSite } = useContext(SiteLookupMapContext);
  const isSelected = selectedSite?.overviewSelected;

  const overviewData: OverviewData = useMemo(() => computeOverviewData(sites), [sites]);

  function isAllowedPricingModel(pricingModel: string | undefined): boolean {
    const ALLOWED_PRICING_MODELS = new Set(['DYNAMIC', 'FIXED', 'TOU', 'POWER']);
    return !!pricingModel && ALLOWED_PRICING_MODELS.has(pricingModel);
  }

  function computeOverviewData(sites: readonly Site[]): OverviewData {
    return {
      numSites: sites.length,
      numSitesTracked: sites.filter((site) => site.trackingState === 'TRACKED').length,
      numPorts: sites.reduce((acc, site) => acc + (site.portsCount || 0), 0),
      numPortsTracked: sites.filter((site) => site.trackingState === 'TRACKED').reduce((acc, site) => acc + (site.portsCount || 0), 0),
      avgPrice: computeAvgPrice(sites),
    };
  }

  const aggregateHistogramBuckets: HistogramBucket[] = useMemo(
    () =>
      (aggregateData?.buckets ?? []).map((b) => ({
        endDate: String(b.endDate),
        percentageUtilization: b.percentageUtilization ?? undefined,
        price: b.price ?? undefined,
        minPrice: b.minPrice ?? undefined,
        maxPrice: b.maxPrice ?? undefined,
      })),
    [aggregateData],
  );

  function computeAvgPrice(sites: readonly Site[]): number | undefined {
    const latestPrices = sites.map((site) => {
      const utilizationBuckets = site.historicalView?.utilizationSeries?.buckets;
      if (!site.historicalView || !isAllowedPricingModel(site.historicalView.pricingModel) || !utilizationBuckets || utilizationBuckets.length === 0) {
        return undefined;
      }
      return utilizationBuckets[utilizationBuckets.length - 1].price;
    });

    return computeRobustAverage(latestPrices);
  }

  const handleClick = () => {
    if (isSelected) {
      // already selected → unselect → collapse
      setSelectedSite(null);
    } else {
      // select new site
      setSelectedSite({ origin: 'list', overviewSelected: true });
    }
  };

  // Local ref + expose it via forwardRef
  const rootRef = useRef<HTMLDivElement | null>(null);
  useImperativeHandle(ref, () => rootRef.current as HTMLDivElement, []);

  // Smoothly scroll the card to the top when it expands
  const cardRef = useRef<HTMLDivElement>(null);

  // Merge refs so parents can still hold a ref to the element
  useEffect(() => {
    if (!ref) return;
    if (typeof ref === 'function') ref(cardRef.current);
    else (ref as MutableRefObject<HTMLDivElement | null>).current = cardRef.current;
  }, [ref]);

  const rowSx = {
    height: 64,
    py: 1.1, // vertical padding similar to your station rows
    alignItems: 'center',
  };

  const dividerSx = {
    borderColor: 'divider',
    mt: 1,
  };

  return (
    <Stack
      ref={rootRef}
      paddingBottom={0}
      paddingX={1.5}
      gap={0.75}
      sx={{
        backgroundColor: isSelected ? '#f6f6fd' : 'inherit',
        borderRadius: borderRadius.lg,
        cursor: 'pointer',
        marginTop: isSelected ? '-9px' : '',
        paddingTop: isSelected ? '17px' : 1,
      }}
      onClick={handleClick}
    >
      <Stack direction="row" gap={1} sx={rowSx}>
        <Tooltip title="overview" placement="top" arrow>
          <Box sx={{ height: 44, width: 44, borderRadius: 0.5, overflow: 'hidden', flexShrink: 0, pl: 0.5 }}>
            <LogoParen height={40} width={40} full={false} viewBox="0 0 209 209" />
          </Box>
        </Tooltip>
        <Stack direction="row" alignItems="center" sx={{ flex: 1, minWidth: 0 }} gap={0.5}>
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
            {isSelected ? <ExpandMore fontSize="small" sx={{ mr: 0.5 }} /> : <ChevronRight fontSize="small" sx={{ mr: 0.5 }} />}
            <Typography
              sx={{
                marginRight: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: { xs: '9ch', lg: '14ch', xl: '18ch' },
                width: 'fit-content',
                whiteSpace: 'nowrap',
                textTransform: 'capitalize',
              }}
              variant="body1"
              fontWeight={600}
            >
              <strong>{t('siteLookup.overview.areasummary')}</strong>
            </Typography>
          </Box>
        </Stack>
      </Stack>
      {isSelected && overviewData && (
        <Stack>
          <Typography sx={{ marginLeft: 3, marginBottom: 1 }} variant="body2">
            <Box>
              <strong>{t('siteLookup.overview.stationsCountTitle')}</strong>
              {t('siteLookup.overview.stationsCount', { numSites: overviewData.numSites, numSitesTracked: overviewData.numSitesTracked })}
            </Box>
            <Box>
              <strong>{t('siteLookup.overview.portsCountTitle')}</strong>
              {t('siteLookup.overview.portsCount', { numPorts: overviewData.numPorts, numPortsTracked: overviewData.numPortsTracked })}
            </Box>
            <Box>
              <strong>{t('siteLookup.overview.averagePriceTitle')}</strong>
              {t('siteLookup.overview.averagePrice', { averagePrice: overviewData.avgPrice !== undefined ? overviewData.avgPrice.toFixed(2) : '-' })}
            </Box>
          </Typography>
          <HistoricalUtilizationHistogram siteId={'overview'} buckets={aggregateHistogramBuckets} includeMinMax={true} />
        </Stack>
      )}
      <Divider sx={dividerSx} />
    </Stack>
  );
});

export default OverviewCard;
